/*
 * *****************************************************************************
 *  Copyright (C)  Motorola Solutions, INC.
 *  All Rights Reserved.
 *  Motorola Solutions Confidential Restricted.
 *  ******************************************************************************
 */

import { Injectable } from '@angular/core';
import { UserService } from '../services/user.service';
import { Actions, createEffect, ofType, OnInitEffects } from '@ngrx/effects';
import { EMPTY, mergeMap, Observable, of, retry, timer } from 'rxjs';
import { catchError, delay, exhaustMap, filter, map, switchMap, tap, throttleTime, withLatestFrom } from 'rxjs/operators';
import { AcdService } from '../services/acd.service';
import { AgentStatus } from 'CalltakingCoreApi';
import {
    acdAnswerCompleteFailed,
    acdAnswerRequest,
    acdAnswerRequestFail,
    acdAnswerRequestSuccess,
    acdAutoAnswer,
    acdBid,
    acdBidFail,
    acdBidRemove,
    acdBidResponse,
    acdRejection,
    acdRequestRejection,
    acdRequestRejectionFail,
    acdRequestRejectionSuccess,
    acdStatusChange,
    acdStatusChangeFail,
    acdStatusChangeSuccess,
    acknowledgeAbandonedRequest,
    clusterLogoutRequest,
    clusterLogoutRequestFail,
    clusterLogoutRequestSuccess,
    connectionStatus,
    connectionStatusFail,
    connectionStatusSuccess,
    deleteManyUsers,
    deleteUserAuthenticationRecord,
    fetchUserAuthenticationRecords,
    fetchUserAuthenticationRecordsFail,
    fetchUserAuthenticationRecordsSuccess,
    initializeUserState,
    initUser,
    logoutRequest,
    noAction,
    remoteLogoutEvent,
    removeIgnoredAcdCall,
    requestedAcdStatusChange,
    setIgnoredAcdCalls,
    setManyUsers,
    updateCurrentRole,
    updateCurrentRoleSuccess,
    userEffectsInitialized
} from './user.actions';
import { AcdStatusService } from '../services/acd-status.service';
import { deleteCall, endObserveAgent, observeAgent, setObservationState } from '../../call/+state/call.actions';
import {
    selectAbandonedAcknowledgeCallId,
    selectAssignedCalls,
    selectCall,
    selectCallsMap,
    selectMyCalls,
    selectObservedUsername
} from '../../call/+state/call.selectors';
import { Action, Store } from '@ngrx/store';
import {
    selectAgentStatus,
    selectBiddingState,
    selectBidResult,
    selectHasPendingAcdAnswer,
    selectMemoizedUserAuthenticationRecord,
    selectNotReadyOnOutbound,
    selectNotReadyOnRelease,
    selectPendingAcdAnswer,
    selectPositionId,
    selectRequestedAgentStatus,
    selectRole,
    selectSub,
    selectSystemReasonCodes,
    selectUsersByClusterName
} from './user.selectors';
import { selectAcdStatusWidgetEnabled, selectCallQueueMap, selectClusterNames } from '../../configuration/+state/configuration.selectors';
import { fetchConsolePosition, fetchUiLogStreamingConfiguration, hotKeyTriggered, loggedAtDifferentPosition } from '../../configuration/+state/configuration.actions';
import { ModalService, ToastType } from '@msi/cobalt';
import { displayToastNotification } from '../../notification/+state/notification.actions';
import { BidResult } from '../model/bid-result';
import { SkipperService } from '../../core/services/skipper.service';
import { concatLatestFrom } from '@ngrx/operators';
import { HotKeyAction } from '../../core/model/hotkey';
import { selectIsHubConnectedAndHeadsetDisconnected } from '../../call/+state/cchub.selectors';
import { SessionLogoutComponent } from '../session-logout/session-logout-component';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { PendoService } from '../../core/pendo/pendo.service';
import { PresenceService } from '../services/presence.service';
import { UserFunctions } from '../util/user-functions';
import { CallFunctions } from '../../call/util/call-functions';
import { ReasonCodeService } from '../services/reason-code.service';
import {
    fetchReasonCodes,
    fetchReasonCodesFail,
    fetchReasonCodesSuccess,
    fetchSystemReasonCodes,
    fetchSystemReasonCodesFail,
    fetchSystemReasonCodesSuccess
} from './reason-code.actions';
import { CoreFunctions } from '../../core/util/core-functions';
import { findSystemReasonLabel, SYSTEM_REASON_CODE } from '../util/system-reason-code.util';
import { AcdReadyStatus } from '../model/requested-agent-status';
import { EnvironmentService } from '../../core/services/environment.service';
import { findBidResultForRejection } from './user.reducer';

@Injectable()
export class UserEffects implements OnInitEffects {
    constructor(
        private userService: UserService,
        private acdService: AcdService,
        private acdStatusService: AcdStatusService,
        private presenceService: PresenceService,
        private skipperService: SkipperService,
        private reasonCodeService: ReasonCodeService,
        private actions$: Actions,
        private store: Store,
        private modalService: ModalService,
        private pendoService: PendoService,
        private env: EnvironmentService
    ) {}

    ngrxOnInitEffects(): Action {
        return userEffectsInitialized();
    }

    initializeUserState$ = createEffect(() =>
        this.actions$.pipe(
            ofType(initializeUserState),
            map(({ clusterName, initializationTime }) => fetchUserAuthenticationRecords({ clusterName: clusterName, initializationTime: initializationTime }))
        )
    );

    fetchUserAuthenticationRecords$: Observable<{}> = createEffect(() =>
        this.actions$.pipe(
            ofType(fetchUserAuthenticationRecords),
            mergeMap(({ clusterName, initializationTime }) =>
                this.userService.requestUserAuthenticationRecords(clusterName).pipe(
                    map((response) =>
                        fetchUserAuthenticationRecordsSuccess({
                            users: response,
                            clusterName: clusterName,
                            initializationTime: initializationTime
                        })
                    ),
                    catchError((err: Error) =>
                        of(fetchUserAuthenticationRecordsFail({ payload: err.message, clusterName: clusterName, initializationTime: initializationTime }))
                    )
                )
            )
        )
    );

    // Records timestamped prior to the websocket subscription initialization are stale and may be orphaned records
    // the fetch (initiated post subscription initialization) should rehydrate any discarded stale records.
    // in the event of a fetch failure, the client should eventually resynchronize user state via websocket updates
    discardStaleUserRecords$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fetchUserAuthenticationRecordsSuccess, fetchUserAuthenticationRecordsFail),
            concatLatestFrom(({ clusterName }) => this.store.select(selectUsersByClusterName(clusterName))),
            mergeMap(([{ clusterName, initializationTime }, existingUsers]) => [
                deleteManyUsers({ userIds: CoreFunctions.findStaleRecords(existingUsers, initializationTime), clusterName: clusterName })
            ])
        )
    );

    fetchUsersSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fetchUserAuthenticationRecordsSuccess),
            mergeMap(({ users, clusterName }) => of(setManyUsers({ users: users, clusterName: clusterName })))
        )
    );

    reportMediaStatusChange$: Observable<{}> = createEffect(() =>
        this.actions$.pipe(
            ofType(connectionStatus),
            concatLatestFrom(() => this.store.select(selectMemoizedUserAuthenticationRecord)),
            filter(([, user]) => !!user),
            switchMap(([{ status }, user]) =>
                this.userService.reportConnectionStatus(status, user.clusterName).pipe(
                    map(() => connectionStatusSuccess()),
                    catchError((err: Error) => of(connectionStatusFail({ payload: err.message })))
                )
            )
        )
    );

    requestLogout$: Observable<{}> = createEffect(() =>
        this.actions$.pipe(
            ofType(logoutRequest),
            concatLatestFrom(() => this.store.select(selectClusterNames)),
            mergeMap(([action, clusterNames]) =>
                clusterNames.map((clusterName) =>
                    clusterLogoutRequest({
                        clusterName: clusterName,
                        forced: action.forced
                    })
                )
            )
        )
    );

    clusterLogoutRequest$: Observable<{}> = createEffect(() =>
        this.actions$.pipe(
            ofType(clusterLogoutRequest),
            mergeMap(({ clusterName, forced }) =>
                this.userService.sendLogoutRequest(clusterName, forced).pipe(
                    map(() => clusterLogoutRequestSuccess({ clusterName: clusterName, forced: forced })),
                    catchError((err: HttpErrorResponse) =>
                        of(
                            clusterLogoutRequestFail({
                                clusterName: clusterName,
                                forced: forced,
                                onCall: err.status === HttpStatusCode.UnprocessableEntity,
                                payload: err.message
                            })
                        )
                    )
                )
            )
        )
    );

    loggedAtDifferentPositionWindow$: Observable<{}> = createEffect(
        () =>
            this.actions$.pipe(
                ofType(loggedAtDifferentPosition),
                tap(() =>
                    this.modalService.open(SessionLogoutComponent, {
                        hasBackdrop: true,
                        disableClose: true,
                        minWidth: '25vw'
                    })
                )
            ),
        { dispatch: false }
    );

    acdStatusChange$: Observable<{}> = createEffect(() =>
        this.actions$.pipe(
            ofType(acdStatusChange),
            filter(({ requestedAgentStatus }) => !!requestedAgentStatus.clusterName),
            switchMap(({ requestedAgentStatus }) =>
                this.acdStatusService.reportAgentStatus(requestedAgentStatus).pipe(
                    retry({ delay: () => timer(2000) }),
                    map((response) => acdStatusChangeSuccess({ agentStatus: response })),
                    catchError((err: Error) => of(acdStatusChangeFail({ payload: err.message })))
                )
            )
        )
    );

    acdStatusChangeFail$: Observable<{}> = createEffect(() =>
        this.actions$.pipe(
            ofType(acdStatusChangeFail),
            map(() => displayToastNotification({ message: 'ACD status change failed', level: ToastType.error }))
        )
    );

    fetchSystemReasonCodes$: Observable<{}> = createEffect(() =>
        this.actions$.pipe(
            ofType(fetchSystemReasonCodes),
            switchMap(() =>
                this.reasonCodeService.fetchSystemReasonCodes().pipe(
                    map((codes) => fetchSystemReasonCodesSuccess({ systemReasonCodes: codes })),
                    catchError((err: Error) => of(fetchSystemReasonCodesFail({ payload: err.message })))
                )
            )
        )
    );

    fetchReasonCodes$: Observable<{}> = createEffect(() =>
        this.actions$.pipe(
            ofType(fetchReasonCodes),
            switchMap(() =>
                this.reasonCodeService.fetchCustomReasonCodes().pipe(
                    map((codes) => fetchReasonCodesSuccess({ reasonCodes: codes })),
                    catchError((err: Error) => of(fetchReasonCodesFail({ payload: err.message })))
                )
            )
        )
    );

    fetchReasonCodesOnInit$: Observable<{}> = createEffect(() =>
        this.actions$.pipe(
            ofType(initializeUserState),
            filter(() => this.env.isNrrcGloballyEnabled()),
            map(() => fetchReasonCodes())
        )
    );

    fetchSystemReasonCodesOnInit$: Observable<{}> = createEffect(() =>
        this.actions$.pipe(
            ofType(initializeUserState),
            filter(() => this.env.isNrrcGloballyEnabled()),
            map(() => fetchSystemReasonCodes())
        )
    );

    overrideHeldCalls$: Observable<{}> = createEffect(() =>
        this.actions$.pipe(
            ofType(requestedAcdStatusChange),
            filter(({ overrideHeldCalls }) => Boolean(overrideHeldCalls)),
            concatLatestFrom(() => this.store.select(selectMyCalls)),
            map(([{ overrideHeldCalls }, myCalls]) => setIgnoredAcdCalls({ calls: myCalls.map((c) => c.uuid) }))
        )
    );

    ignoreHeldCallsCleanup$: Observable<{}> = createEffect(() =>
        this.actions$.pipe(
            ofType(deleteCall),
            map(({ call }) => removeIgnoredAcdCall({ callId: call.uuid }))
        )
    );

    acdBid$: Observable<{}> = createEffect(() =>
        this.actions$.pipe(
            ofType(acdBid),
            concatLatestFrom(({ bid }) => [this.store.select(selectCallQueueMap), this.store.select(selectBidResult(bid.auctionId))]),
            filter(([, , bidResult]) => !bidResult),
            exhaustMap(([{ bid }, callQueueMap]) => {
                console.debug(`Attempting ACD Bid: callId: ${bid.callId}`);
                return this.acdService.bidForCall(bid.callId, bid.clusterName, bid.selectionTime, bid.previouslyAssigned).pipe(
                    map((response) => {
                        let result = response as BidResult;
                        result.clusterName = bid.clusterName;
                        result.nenaId = bid.nenaId;
                        result.queueACD = response.queueName ? callQueueMap[response.queueName].queueACD : undefined;
                        result.previouslyAssigned = bid.previouslyAssigned;
                        // associate the bid result with the auction and queueName of the originating bid request
                        // these fields are not populated in certain responses (ie, no_auction_for_call)
                        result.auctionId = result.auctionId ? result.auctionId : bid.queueJoin.auctionId;
                        result.queueName = result.queueName ? result.queueName : bid.queueJoin.queueName;
                        console.debug(`ACD Bid Result: ${response.result} for callId: ${response.callId}`);
                        return acdBidResponse({ result: result });
                    }),
                    catchError((err: Error) => {
                        console.error(`ACD Bid Error: ${err.message}`);
                        return of(acdBidFail({ payload: err.message }));
                    })
                );
            })
        )
    );

    acdBidCleanup$: Observable<{}> = createEffect(() =>
        this.actions$.pipe(
            ofType(deleteCall),
            map(({ call }) => acdBidRemove({ callId: call.uuid }))
        )
    );

    acdAutoAnswer$: Observable<{}> = createEffect(() =>
        this.actions$.pipe(
            ofType(acdAutoAnswer),
            concatLatestFrom(({ bid }) => this.store.select(selectCall(bid.callId))),
            map(([{ bid }, call]) =>
                CallFunctions.isAbandoned(call.status)
                    ? acknowledgeAbandonedRequest({ callId: bid.callId, clusterName: bid.clusterName })
                    : acdAnswerRequest({ bid })
            )
        )
    );

    acdAnswer$: Observable<{}> = createEffect(() =>
        this.actions$.pipe(
            ofType(acdAnswerRequest),
            concatLatestFrom(() => this.store.select(selectHasPendingAcdAnswer)),
            filter(([, pendingAcdAnswer]) => !pendingAcdAnswer),
            exhaustMap(([{ bid }]) => {
                console.debug(`Attempting ACD Answer: callId: ${bid.callId}`);
                return this.acdService.acdAnswer(bid.callId, bid.clusterName, bid.queueName).pipe(
                    map(() => {
                        console.debug(`ACD Answer Successful: callId: ${bid.callId}`);
                        return acdAnswerRequestSuccess({
                            callId: bid.callId,
                            nenaId: bid.nenaId,
                            clusterName: bid.clusterName,
                            auctionId: bid.auctionId
                        });
                    }),
                    catchError((err: Error) => {
                        console.error(`ACD Answer Failure: callId: ${bid.callId}, error: ${err.message}`);
                        return of(acdAnswerRequestFail({ callId: bid.callId, payload: err.message }));
                    })
                );
            })
        )
    );

    acdReject$: Observable<{}> = createEffect(() =>
        this.actions$.pipe(
            ofType(acdRequestRejection),
            concatLatestFrom(() => this.store.select(selectCallsMap)),
            switchMap(([{ callId }, callMap]) => {
                let call = callMap[callId];
                console.debug(`Attempting ACD Reject: callId: ${callId}`);
                return this.acdService.acdReject(callId, call?.clusterName).pipe(
                    map(() => {
                        console.debug(`ACD Reject Successful: callId: ${callId}`);
                        return acdRequestRejectionSuccess({ callId: callId });
                    }),
                    catchError((err: Error) => of(acdRequestRejectionFail({ payload: err.message })))
                );
            })
        )
    );

    acdRejection$: Observable<{}> = createEffect(() =>
        this.actions$.pipe(
            ofType(acdRejection),
            concatLatestFrom(() => [
                this.store.select(selectBiddingState),
                this.store.select(selectAgentStatus),
                this.store.select(selectAbandonedAcknowledgeCallId),
                this.store.select(selectSystemReasonCodes)
            ]),
            mergeMap(([{ event }, biddingState, agentStatus, acknowledgeAbandonedCallId, systemReasonCodes]) => {
                const notReadyOnFailure = findBidResultForRejection(biddingState, event)?.queueACD?.answerMethod?.notReadyOnFailure;
                const isAgentAssignedCall = ['ASSIGNED_CALL', 'ASSIGNED_ABANDONED_CALL', 'READY'].includes(agentStatus);
                const isNotAcknowledgedAbandonedCall = acknowledgeAbandonedCallId !== event.callId;
                const isOfferTimeout = event.reason === 'OFFER_TIMEOUT';
                const shouldForceOut = notReadyOnFailure && isAgentAssignedCall && isNotAcknowledgedAbandonedCall && isOfferTimeout;

                if (shouldForceOut) {
                    return [
                        requestedAcdStatusChange({
                            agentStatus: 'NOT_READY',
                            statusReasonDisplayName: findSystemReasonLabel(systemReasonCodes, SYSTEM_REASON_CODE.noAnswer, 'No Answer'),
                            statusReasonId: SYSTEM_REASON_CODE.noAnswer.code,
                            isAgentRequested: false
                        }),
                        displayToastNotification({
                            message: 'System triggered NOT READY due to an unanswered ACD call. Manual status change required.',
                            level: ToastType.error,
                            options: {
                                autoDismiss: false
                            }
                        })
                    ];
                }
                return EMPTY;
            })
        )
    );

    requestAcknowledge$: Observable<{}> = createEffect(() =>
        this.actions$.pipe(
            ofType(acknowledgeAbandonedRequest),
            concatLatestFrom(() => [
                this.store.select(selectBiddingState),
                this.store.select(selectAgentStatus),
                this.store.select(selectAbandonedAcknowledgeCallId),
                this.store.select(selectSystemReasonCodes)
            ]),
            mergeMap(([{ callId, clusterName }, biddingState, agentStatus, acknowledgeAbandonedCallId, systemReasonCodes]) => [
                requestedAcdStatusChange({
                    agentStatus: 'NOT_READY',
                    statusReasonDisplayName: findSystemReasonLabel(systemReasonCodes, SYSTEM_REASON_CODE.abandoned),
                    isAgentRequested: false
                })
            ])
        )
    );


    // Clean up pending acd answers that never complete
    timeOutPendingAcdAnswer$: Observable<{}> = createEffect(() => {
            return this.actions$.pipe(
                ofType(acdAnswerRequestSuccess),
                delay(5000),
                concatLatestFrom(() => this.store.select(selectPendingAcdAnswer)),
                filter(([{ callId }, pendingAnswer]) => !!pendingAnswer && pendingAnswer.callId === callId),
                map(([{ callId, clusterName }, pendingAnswer]) => acdAnswerCompleteFailed({ callId, clusterName }))
            );
        }
    );

    fetchSkipperConsolePositionConfiguration$ = createEffect(() =>
        this.actions$.pipe(
            ofType(initUser),
            switchMap(() => this.skipperService.initialized$),
            filter((initialized) => !!initialized),
            concatLatestFrom(() => this.store.select(selectPositionId)),
            mergeMap(([, position]) =>
                this.env.environment?.featureFlags?.uiLogStreaming
                    ? [fetchConsolePosition({ position }), fetchUiLogStreamingConfiguration({ position })]
                    : [fetchConsolePosition({ position })]
            )
        )
    );

    notifyServiceWorkersOfTokenUpdate$: Observable<{}> = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(initUser),
                tap(({ token }) =>
                    navigator.serviceWorker.ready
                        .then(() =>
                            navigator.serviceWorker
                                .getRegistrations()
                                .then((foo) => foo.forEach((reg) => reg.active?.postMessage({ token: token })))
                                .then(() => console.debug('Token sent to Service Worker'))
                                .catch((e) => console.error('Failed to send token information to the service worker: %', e))
                        )
                        .catch((e) => console.error('Service worker ready check error: %', e))
                )
            );
        },
        { dispatch: false }
    );

    acdAnswerFailureReject$: Observable<{}> = createEffect(() =>
        this.actions$.pipe(
            ofType(acdAnswerCompleteFailed),
            map(({ callId }) => acdRequestRejection({ callId }))
        )
    );

    notReadyOnRelease$: Observable<{}> = createEffect(() =>
        this.actions$.pipe(
            ofType(acdStatusChangeSuccess),
            concatLatestFrom(() => [
                this.store.select(selectNotReadyOnRelease),
                this.store.select(selectSystemReasonCodes)
            ]),
            filter(([{ agentStatus }, notReadyOnRelease]) => notReadyOnRelease && agentStatus.status === 'ON_ACD_CALL'),
            map(([, , systemReasonCodes]) =>
                requestedAcdStatusChange({
                    agentStatus: AcdReadyStatus.NOT_READY,
                    statusReasonDisplayName: findSystemReasonLabel(systemReasonCodes, SYSTEM_REASON_CODE.notReadyCallEnded)
                })
            )
        )
    );

    notReadyOnOutbound$: Observable<{}> = createEffect(() =>
        this.actions$.pipe(
            ofType(acdStatusChangeSuccess),
            concatLatestFrom(() => [
                this.store.select(selectNotReadyOnOutbound),
                this.store.select(selectSystemReasonCodes)
            ]),
            filter(([{ agentStatus }, notReadyOnOutbound]) => notReadyOnOutbound && agentStatus.status === 'ON_CALL'),
            map(([{ agentStatus }, notReadyOnOutbound, systemReasonCodes]) => {
                return requestedAcdStatusChange({
                    agentStatus: AcdReadyStatus.NOT_READY,
                    statusReasonDisplayName: findSystemReasonLabel(systemReasonCodes, SYSTEM_REASON_CODE.notReadyCallEnded)
                });
            })
        )
    );

    rejectCallAssignmentsOnNotReady$: Observable<{}> = createEffect(() =>
        this.actions$.pipe(
            ofType(acdStatusChangeSuccess),
            filter(({ agentStatus }) => agentStatus.status === 'NOT_READY' && agentStatus.agentRequestedStatus),
            concatLatestFrom(() => [this.store.select(selectAssignedCalls)]),
            filter(([, assignedCalls]) => Boolean(assignedCalls.length)),
            mergeMap(([, assignedCalls]) => assignedCalls.map((assignedCall) => acdRequestRejection({ callId: assignedCall.uuid })))
        )
    );

    acdReadyToggleHotkeyHandler$: Observable<{}> = createEffect(() =>
        this.actions$.pipe(
            ofType(hotKeyTriggered),
            filter(({ hotkey }) => hotkey.action === HotKeyAction.READY_TOGGLE),
            concatLatestFrom(() => [
                this.store.select(selectRequestedAgentStatus),
                this.store.select(selectIsHubConnectedAndHeadsetDisconnected),
                this.store.select(selectAcdStatusWidgetEnabled),
                this.store.select(selectSystemReasonCodes)
            ]),
            filter(([, , hubConnectedAndHeadsetDisconnected, acdWidgetEnabled]) => !hubConnectedAndHeadsetDisconnected && !acdWidgetEnabled),
            map(([, { status }, , , systemReasonCodes]) => {
                const targetStatus = status === AcdReadyStatus.NOT_READY ? AcdReadyStatus.READY : AcdReadyStatus.NOT_READY;
                return requestedAcdStatusChange({
                    agentStatus: targetStatus,
                    isAgentRequested: true,
                    statusReasonDisplayName: findSystemReasonLabel(systemReasonCodes, targetStatus === AcdReadyStatus.READY ? SYSTEM_REASON_CODE.readyManualChange : SYSTEM_REASON_CODE.notReadyManualChange)
                });
            })
        )
    );

    acdStatusHotkeyHandler$: Observable<{}> = createEffect(() =>
        this.actions$.pipe(
            ofType(hotKeyTriggered),
            filter(({ hotkey }) => hotkey.action === HotKeyAction.STATUS),
            concatLatestFrom(() => [
                this.store.select(selectAcdStatusWidgetEnabled),
                this.store.select(selectSystemReasonCodes)
            ]),
            filter(([, acdWidgetEnabled]) => !acdWidgetEnabled),
            map(([{ hotkey }, , systemReasonCodes]) => {
                const targetStatus = hotkey.params[0] as AgentStatus;
                return requestedAcdStatusChange({
                    agentStatus: targetStatus,
                    isAgentRequested: true,
                    statusReasonDisplayName: findSystemReasonLabel(
                        systemReasonCodes,
                        targetStatus === AcdReadyStatus.READY ? SYSTEM_REASON_CODE.readyManualChange : SYSTEM_REASON_CODE.notReadyManualChange
                    )
                });
            })
        )
    );

    autoLogoffQueued$: Observable<{}> = createEffect(() =>
        this.actions$.pipe(
            ofType(remoteLogoutEvent),
            // throttle a little bit to discard duplicated forced logout messages from CTC coming from local subscriptions
            throttleTime(300),
            map(({ logoutRequest }) =>
                displayToastNotification({
                    level: ToastType.warning,
                    message: (logoutRequest.forcedLogout ? 'Forced ' : 'Remote ') + 'Logoff Initiated'
                })
            )
        )
    );
    updateCurrentRole$ = createEffect(() =>
        this.actions$.pipe(
            ofType(updateCurrentRole),
            withLatestFrom(this.store.select(selectSub), this.store.select(selectRole)),
            switchMap(([action, sub, currentRole]) => {
                if (sub && action.role !== currentRole) {
                    return this.skipperService.updateCurrentRole(action.role, sub).pipe(
                        map(() => updateCurrentRoleSuccess()),
                        catchError((err: Error) => of(noAction()))
                    );
                } else {
                    return of(noAction());
                }
            })
        )
    );
    updateCurrentRoleSuccess$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(updateCurrentRoleSuccess),
                tap(() => {
                    window.location.reload();
                })
            ),
        { dispatch: false }
    );

    startObservationAcdStatus$ = createEffect(() =>
        this.actions$.pipe(
            ofType(observeAgent),
            concatLatestFrom(() => this.store.select(selectSystemReasonCodes)),
            map(([, systemReasonCodes]) =>
                this.env.isNrrcGloballyEnabled()
                    ? requestedAcdStatusChange({
                        agentStatus: 'NOT_READY',
                        statusReasonDisplayName: findSystemReasonLabel(systemReasonCodes, SYSTEM_REASON_CODE.observing),
                        statusReasonId: SYSTEM_REASON_CODE.observing.code,
                        isAgentRequested: false
                    })
                    : requestedAcdStatusChange({ agentStatus: 'NOT_READY', isAgentRequested: false })
            )
        )
    );

    resumeObservationAcdStatus$ = createEffect(() =>
        this.actions$.pipe(
            ofType(setObservationState),
            filter(({ observationState }) => observationState === 'active'),
            concatLatestFrom(() => this.store.select(selectSystemReasonCodes)),
            map(([, systemReasonCodes]) =>
                this.env.isNrrcGloballyEnabled()
                    ? requestedAcdStatusChange({
                        agentStatus: 'NOT_READY',
                        statusReasonDisplayName: findSystemReasonLabel(systemReasonCodes, SYSTEM_REASON_CODE.observing),
                        statusReasonId: SYSTEM_REASON_CODE.observing.code,
                        isAgentRequested: false
                    })
                    : requestedAcdStatusChange({ agentStatus: 'NOT_READY', isAgentRequested: false })
            )
        )
    );

    endObservationAcdStatus$ = createEffect(() =>
        this.actions$.pipe(
            ofType(endObserveAgent),
            concatLatestFrom(() => this.store.select(selectSystemReasonCodes)),
            map(([, systemReasonCodes]) =>
                this.env.isNrrcGloballyEnabled()
                    ? requestedAcdStatusChange({
                        agentStatus: 'NOT_READY',
                        statusReasonDisplayName: findSystemReasonLabel(systemReasonCodes, SYSTEM_REASON_CODE.observingEnded),
                        statusReasonId: SYSTEM_REASON_CODE.observingEnded.code,
                        isAgentRequested: false
                    })
                    : requestedAcdStatusChange({ agentStatus: 'NOT_READY', isAgentRequested: false })
            )
        )
    );

    stopMonitorOnObserveeLogout$: Observable<{}> = createEffect(() =>
        this.actions$.pipe(
            ofType(deleteUserAuthenticationRecord),
            withLatestFrom(this.store.select(selectObservedUsername)),
            filter(([action, observee]) => action.userAuthenticationRecord.username === observee),
            mergeMap(([action]) => [
                endObserveAgent(),
                displayToastNotification({
                    message: `Observe ended: ${UserFunctions.formatName(action.userAuthenticationRecord)} has logged out.`,
                    level: ToastType.information
                })
            ])
        )
    );

}
